import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { D, designError, fmtDate } from "@/lib/designApi";
import { DataTable, NoAccess, PageHeader, StatusBadge } from "@/components/design/common";
import { queueLink } from "@/pages/design/DesignDashboard";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const STEP_TEXT = {
  check: "Check", approve: "Approve", release: "Release", implement: "Implement ECN", review: "Review ECR",
  gate: "Gate decision", work: "Work on request", assign: "Assign request",
};

export default function DesignApprovals() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState("");
  const nav = useNavigate();
  const load = () => api.get(`${D}/approvals/queue`).then((r) => setRows(r.data)).catch((e) => setErr(designError(e)));
  useEffect(() => { load(); }, []);
  if (err) return <NoAccess error={err} />;
  const steps = [...new Set((rows || []).map((r) => r.step))];
  const shown = (rows || []).filter((r) => !step || r.step === step);
  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-approvals">
      <PageHeader title="My Queue" subtitle="Everything waiting on you — Maker → Checker → Approver, releases, ECRs, NPD gates and assigned requests (oldest first)">
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>
      </PageHeader>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant={step ? "outline" : "default"} onClick={() => setStep("")}>All ({rows?.length || 0})</Button>
        {steps.map((s) => (
          <Button key={s} size="sm" variant={step === s ? "default" : "outline"} onClick={() => setStep(s)}>
            {STEP_TEXT[s] || s} ({rows.filter((r) => r.step === s).length})
          </Button>
        ))}
      </div>
      <DataTable rows={shown} testid="design-queue-table" empty={rows ? "Nothing waiting on you." : "Loading…"}
        onRowClick={(r) => nav(queueLink(r))}
        columns={[
          { key: "step", title: "Action", render: (r) => <span className="uppercase text-[11px] font-semibold text-blue-700">{STEP_TEXT[r.step] || r.step}</span> },
          { key: "entity_label", title: "Type" },
          { key: "number", title: "Number", render: (r) => <span className="mono">{r.number}{r.revision ? ` Rev ${r.revision}` : ""}</span> },
          { key: "title", title: "Title", cellClass: "max-w-md truncate" },
          { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} /> },
          { key: "maker", title: "From" },
          { key: "submitted_at", title: "Since", render: (r) => fmtDate(r.submitted_at) },
          { key: "age_days", title: "Age (d)", render: (r) => <span className={r.age_days > 7 ? "text-red-600 font-semibold mono" : "mono"}>{r.age_days ?? "—"}</span> },
        ]} />
    </div>
  );
}
